import type { TFunction } from 'i18next';
import type { RuleAction, RuleCondition, RulesModel } from '@modules/configuration/Rules/models/Rules';
import { getCachedCourierIntegrations } from '@modules/configuration/Rules/services/prefetchRulesReferenceData';
import globalCache from '@shared/lib/GlobalCache';

const OP_KEYS: Record<string, string> = {
  '=': 'eq',
  '!=': 'neq',
  '>': 'gt',
  '<': 'lt',
  '>=': 'gte',
  '<=': 'lte',
  starts_with: 'starts_with',
  ends_with: 'ends_with',
  contains: 'contains',
  between: 'between',
};

function humanizeField(field: string): string {
  return field.replace(/_/g, ' ');
}

export function getFieldLabel(field: string, t: TFunction<'rulesModule'>): string {
  const k = `clientList.display.fields.${field}` as const;
  const label = t(k);
  if (label && label !== k) return label;
  return humanizeField(field);
}

export function getOperatorLabel(operator: string, t: TFunction<'rulesModule'>): string {
  const short = OP_KEYS[operator];
  if (short) {
    const k = `clientList.display.ops.${short}` as const;
    const label = t(k);
    if (label && label !== k) return label;
  }
  return operator;
}

type CachedSelectOption = { id: string; name: string };

function resolveSelectValueLabel(field: string, value: string): string | null {
  const raw = globalCache.get<{ options?: CachedSelectOption[] }>(`values:${field}`);
  const opt = raw?.options?.find((o) => String(o.id) === String(value));
  return opt?.name ?? null;
}

/** Normaliza valor crudo y resuelve nombres desde la caché de `prefetch` / `loadValues` cuando exista. */
function formatValueDisplay(field: string, value: string, t: TFunction<'rulesModule'>): string {
  if (field === 'has_coverage') {
    if (value === '1' || value === 'true' || value.toLowerCase() === 'yes') {
      return t('clientList.display.value_yes');
    }
    if (value === '0' || value === 'false' || value.toLowerCase() === 'no') {
      return t('clientList.display.value_no');
    }
  }
  const fromSelect = resolveSelectValueLabel(field, value);
  if (fromSelect) return fromSelect;
  return value;
}

export interface ClientRuleConditionRow {
  id: string;
  field: string;
  fieldLabel: string;
  opLabel: string;
  valueDisplay: string;
}

/**
 * Línea legible: "No enviar" o "Enviar con {integración} - {servicio}" (vista listado cliente).
 */
export function buildActionSummaryLines(actions: RuleAction[] | undefined, t: TFunction<'rulesModule'>): string[] {
  if (!actions?.length) return [];
  return actions.map((a) => {
    if (a.action === 'no_enviar') {
      return t('clientList.display.action_line_no_send');
    }
    if (a.action === 'courier_integration_id') {
      const integ = getCachedCourierIntegrations()?.find((i) => String(i.id) === String(a.value));
      const integration = integ?.name ?? t('summary.courier_id', { id: String(a.value ?? '') });
      if (a.courier_service_id != null && a.value != null && a.value !== '') {
        const prods = globalCache.get<CachedSelectOption[]>(`courierProducts:${String(a.value)}`);
        const serv = prods?.find((p) => String(p.id) === String(a.courier_service_id));
        const service = serv?.name ?? t('summary.service_id', { id: a.courier_service_id });
        return t('clientList.display.action_line_send_with', { integration, service });
      }
      return t('clientList.display.action_line_send_integration_only', { integration });
    }
    return a.action;
  });
}

export function buildClientRuleInfo(rule: RulesModel, t: TFunction<'rulesModule'>) {
  const rawConds: RuleCondition[] = rule.rules?.conditions ?? [];
  const matchAll = rule.rules?.match_type !== 'any';

  const conditions: ClientRuleConditionRow[] = rawConds.map((c, i) => ({
    id: `${c.field}-${i}`,
    field: c.field,
    fieldLabel: getFieldLabel(c.field, t),
    opLabel: getOperatorLabel(c.operator, t),
    valueDisplay: formatValueDisplay(c.field, c.value, t),
  }));

  const actionSummaryLines = buildActionSummaryLines(rule.actions, t);

  return { conditions, matchAll, actionSummaryLines };
}
